import {Component, Input, OnInit, inject} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators, ReactiveFormsModule} from '@angular/forms';
import {isEmpty} from '../../../_utils/lang';
import {SubjectFormBaseComponent} from '../subject-form-base.component';
import {dateValidator, phoneValidator, sinValidator} from '../../../_utils/validators';
import {ADDRESS_PATTERN, POSTAL_PATTERN} from '../../../_constants/constants';
import {NgClass} from '@angular/common';
import {HawkSinDobComponent} from './hawk-sin-dob/hawk-sin-dob.component';
import {HawkTypeComponent} from './hawk-type/hawk-type.component';
import {AddressFormComponent} from '../../../common/forms/address-form/address-form.component';
import {PhoneFormRowComponent} from '../../../common/forms/phone-form-row/phone-form-row.component';
import {TranslateModule} from '@ngx-translate/core';

@Component({
    selector: 'app-hawk-form',
    templateUrl: './hawk-form.component.html',
    styleUrls: ['./hawk-form.component.scss'],
    imports: [ReactiveFormsModule, NgClass, HawkSinDobComponent, HawkTypeComponent, AddressFormComponent, PhoneFormRowComponent, TranslateModule]
})
export class HawkFormComponent extends SubjectFormBaseComponent implements OnInit {

  @Input() types: Map<string, string>;
  private formBuilder = inject(UntypedFormBuilder);
  
  constructor() {
    super();
    const formBuilder = this.formBuilder;

    const plainAddressFormControls = () => ({type: '', street: '', city: '', province: '', postalCode: '', phone: ''});
    this.f = formBuilder.group({
      sin1:  ['', sinValidator],
      sin2:  ['', sinValidator],
      dob1:  ['', dateValidator],
      dob2:  ['', dateValidator],
      addr1: formBuilder.group(plainAddressFormControls()),
      addr2: formBuilder.group(plainAddressFormControls()),
      addr3: formBuilder.group(plainAddressFormControls())
    });
  }

  private static requiredValidator(required: boolean): ValidatorFn {
    return required ? Validators.required : null;
  }

  private static hrfaAddressValidators(required: boolean): {[key: string]: ValidatorFn} {
    return {
      type:       HawkFormComponent.requiredValidator(required),
      street:     Validators.compose([HawkFormComponent.requiredValidator(required), Validators.maxLength(128), Validators.pattern(ADDRESS_PATTERN)]),
      city:       Validators.compose([HawkFormComponent.requiredValidator(required), Validators.maxLength(32), Validators.pattern(ADDRESS_PATTERN)]),
      province:   HawkFormComponent.requiredValidator(required),
      postalCode: Validators.compose([HawkFormComponent.requiredValidator(required), Validators.maxLength(8), Validators.pattern(POSTAL_PATTERN)]),
      phone:      Validators.compose([HawkFormComponent.requiredValidator(required), Validators.maxLength(16), phoneValidator])
    };
  }

  private static setValidators(formGroup: UntypedFormGroup, config: {[key: string]: ValidatorFn}) {
    Object.entries(config).forEach((e: [string, ValidatorFn]) => {
      formGroup.controls[e[0]].setValidators(e[1]);
      formGroup.controls[e[0]].updateValueAndValidity();
    });
  }

  private static conditionalHrfaAddressRequiredValidators(addrGroup: string, formGroup: UntypedFormGroup) {
    const isAnyFieldSet: boolean = !!Object.values(formGroup?.controls).find(c => !isEmpty(c.value));
    console.debug(`${addrGroup} has any field set: ${isAnyFieldSet}`);
    HawkFormComponent.setValidators(formGroup, HawkFormComponent.hrfaAddressValidators(isAnyFieldSet));
  }

  ngOnInit() {
    HawkFormComponent.setValidators(this.f.controls.addr1 as UntypedFormGroup, HawkFormComponent.hrfaAddressValidators(true));
  }

  onSubmit() {
    HawkFormComponent.conditionalHrfaAddressRequiredValidators('addr2', this.f.controls.addr2 as UntypedFormGroup);
    HawkFormComponent.conditionalHrfaAddressRequiredValidators('addr3', this.f.controls.addr3 as UntypedFormGroup);
    super.onSubmit();
  }

  public get addr1C(): {[p: string]: AbstractControl} {
    return this.addr1F.controls;
  }

  public get addr2C(): {[p: string]: AbstractControl} {
    return this.addr2F.controls;
  }

  public get addr3C(): {[p: string]: AbstractControl} {
    return this.addr3F.controls;
  }

  public get addr1F(): UntypedFormGroup {
    return this.f.controls.addr1 as UntypedFormGroup;
  }

  public get addr2F(): UntypedFormGroup {
    return this.f.controls.addr2 as UntypedFormGroup;
  }

  public get addr3F(): UntypedFormGroup {
    return this.f.controls.addr3 as UntypedFormGroup;
  }

}
